import { getExtraProperty } from '@tribeplatform/gql-client/lib'
import { ExtraPropertyEntityType } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useNetwork,
  useUpdateNetwork,
  useUpsertExtraProperty,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

const makeAbsoluteUrl = (url: string): string => {
  if (!url) {
    return ''
  }

  if (
    !url.startsWith('https://') &&
    !url.startsWith('http://') &&
    !url.startsWith('/')
  ) {
    return `https://${url}`
  }
  return url
}

export const GeneralSettings = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: updateNetwork, isLoading } = useUpdateNetwork()
  const { mutateAsync: upsertExtraProperty, isLoading: isUpserting } =
    useUpsertExtraProperty()

  return (
    <Card id="general-settings">
      <Card.Header
        title={$t({
          defaultMessage: 'General settings',
          id: 'Admin.GeneralSettings',
        })}
      />
      <Card.Content className="flex flex-col space-y-5">
        <Form
          defaultValues={{
            name: network?.name,
            termsOfServiceUrl: getExtraProperty(
              network?.extraProperties,
              'termsOfServiceUrl',
            ),
            privacyPolicyUrl: getExtraProperty(
              network?.extraProperties,
              'privacyPolicyUrl',
            ),
          }}
          onSubmit={async data => {
            const { name, termsOfServiceUrl, privacyPolicyUrl } = data

            const tosPromise = upsertExtraProperty({
              input: {
                key: 'termsOfServiceUrl',
                value: JSON.stringify(makeAbsoluteUrl(termsOfServiceUrl)),
                entityType: ExtraPropertyEntityType.Network,
              },
            })

            const ppPromise = upsertExtraProperty({
              input: {
                key: 'privacyPolicyUrl',
                value: JSON.stringify(makeAbsoluteUrl(privacyPolicyUrl)),
                entityType: ExtraPropertyEntityType.Network,
              },
            })

            const unPromise = updateNetwork({
              input: {
                name,
              },
            })

            Promise.all([tosPromise, ppPromise, unPromise])
              .then(() => {
                toast({
                  title: $t({
                    defaultMessage: 'Settings updated',
                    id: 'Admin.GeneralSettings.Updated',
                  }),
                  status: 'success',
                })
              })
              .catch(() => {
                toast({
                  title: $t({
                    defaultMessage: 'Unable to update settings',
                    id: 'Admin.GeneralSettings.FailedToUpdate',
                  }),
                  status: 'error',
                })
              })
          }}
        >
          <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
            <Form.Input
              name="name"
              label={$t({
                defaultMessage: 'Community name',
                id: 'Admin.GeneralSettings.Name',
              })}
              placeholder={$t({
                defaultMessage: 'ACME Community',
                id: 'Admin.GeneralSettings.Name.Placeholder',
              })}
              validation={{
                minLength: {
                  value: 5,
                  message: $t(
                    {
                      defaultMessage:
                        'Name must be at least {length, number} characters',
                      id: 'Admin.GeneralSettings.Name.MinLengthValidation',
                    },
                    {
                      length: 5,
                    },
                  ),
                },
                maxLength: {
                  value: 50,
                  message: $t(
                    {
                      defaultMessage:
                        'Name must be at most {length, number} characters',
                      id: 'Admin.GeneralSettings.Name.MaxLengthValidation',
                    },
                    {
                      length: 50,
                    },
                  ),
                },
              }}
            />
            <Form.Input
              name="termsOfServiceUrl"
              label={$t({
                defaultMessage: 'Terms of service',
                id: 'Generics.ToS',
              })}
            />
            <Form.Input
              name="privacyPolicyUrl"
              label={$t({
                defaultMessage: 'Privacy policy',
                id: 'Generics.PrivacyPolicy',
              })}
            />
            <Form.Actions>
              <Button type="submit" loading={isLoading || isUpserting}>
                <T defaultMessage="Update" id="Generics.Update" />
              </Button>
            </Form.Actions>
          </div>
        </Form>
      </Card.Content>
    </Card>
  )
}
